import type { ReactNode } from "react";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";

interface PageHeaderProps {
  title: string;
  /** Supporting line under the title, e.g. a row count. Always rendered when
   *  given — CamerasPage/UsersPage used to hide this at zero rows while
   *  ProjectsPage always showed it; that drift is resolved in favor of
   *  always showing it, since "0 accounts" is still real information and
   *  the inconsistency itself was the bug, not either specific choice. */
  description?: ReactNode;
  /** The page's primary action, e.g. an "Add X" Button. */
  actions?: ReactNode;
}

// The header/title/count/primary-action row every management page
// (Cameras/Projects/Users/Roles) repeated with small drifts. Extracted once
// so it can't drift again, and so it's exactly one place a page needs to
// import to get its title row: this component renders unconditionally,
// independent of the page's loading/error/data state, which is the other
// half of the fix — see each page's body-state handling below it. A page
// that returns early from a loading/error branch before rendering this loses
// its heading and its primary action along with the spinner/banner; every
// page in this app now renders PageHeader first and swaps only the content
// beneath it.
export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <HStack justify="between" vAlign="center" wrap="wrap" gap={2}>
      <VStack gap={0.5}>
        <Heading level={1}>{title}</Heading>
        {description && (
          <Text type="supporting" color="secondary">
            {description}
          </Text>
        )}
      </VStack>
      {actions}
    </HStack>
  );
}
